import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import FormControl from '@mui/material/FormControl'
import Grid from '@mui/material/Grid2'
import FilledInput from '@mui/material/FilledInput'
import InputLabel from '@mui/material/InputLabel'
import InputAdornment from '@mui/material/InputAdornment'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import { useForm, Controller } from 'react-hook-form'
import { validateWordCount } from '@utils/validationHelpers'
import { QuoteItemType } from '@generatedTypes/graphql'

export interface HourlyFeeFormData {
  formType: QuoteItemType.Hourly
  hourlyRate: number
  payableAmount: number
  details: string
}

interface HourlyFeeQuoteFormProps {
  minDetailsWordCount?: number
  minHourlyRate?: number
  minPayableAmount?: number
  onSubmit: (data: HourlyFeeFormData) => Promise<boolean>
  onClickCancelButton: () => void
  initialValues?: HourlyFeeFormData
}

const HourlyFeeQuoteForm = ({
  minDetailsWordCount = 0,
  // Stripe has a requirement for min change ammounts of 0.5 USD
  // So setting validation to this ammount to satisfy this requirement
  // https://docs.stripe.com/currencies#minimum-and-maximum-charge-amounts
  minHourlyRate = 0.5,
  minPayableAmount = 0.5,
  onSubmit,
  onClickCancelButton,
  initialValues,
}: HourlyFeeQuoteFormProps) => {
  const {
    handleSubmit,
    control,
    reset,
    formState: { errors },
  } = useForm<HourlyFeeFormData>({
    defaultValues: {
      formType: QuoteItemType.Hourly,
      hourlyRate: initialValues?.hourlyRate ?? 0,
      payableAmount: initialValues?.payableAmount ?? 0,
      details: initialValues?.details ?? '',
    },
  })

  const handleFormSubmit = async (data: HourlyFeeFormData) => {
    const resetForm = await onSubmit(data)
    if (resetForm) {
      reset()
    }
  }

  return (
    <Card>
      <CardContent>
        <Typography variant="h5" gutterBottom mb={2}>
          Hourly Fee
        </Typography>
        <form onSubmit={handleSubmit(handleFormSubmit)} noValidate>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 4 }}>
              <Controller
                name="hourlyRate"
                control={control}
                rules={{
                  required: 'An hourly rate is required',
                  min: {
                    value: minHourlyRate,
                    message: `The minimum hourly rate is ${minHourlyRate}`,
                  },
                }}
                render={({ field }) => (
                  <FormControl fullWidth variant="filled">
                    <InputLabel htmlFor="hourlyRate">
                      Attorney's standard Hourly Rate
                    </InputLabel>
                    <FilledInput
                      {...field}
                      id="hourlyRate"
                      fullWidth
                      type="number"
                      onChange={(e) => {
                        const value = e.target.value
                        field.onChange(value === '' ? '' : Number(value))
                      }}
                      startAdornment={
                        <InputAdornment position="start">$</InputAdornment>
                      }
                      error={!!errors.hourlyRate}
                      slotProps={{ input: { min: 0, step: 10 } }}
                      required
                    />
                    {errors.hourlyRate && (
                      <Typography
                        color="error"
                        variant="caption"
                        sx={{ mt: 1 }}
                      >
                        {errors.hourlyRate.message}
                      </Typography>
                    )}
                  </FormControl>
                )}
              />
              <Controller
                name="payableAmount"
                control={control}
                rules={{
                  required: 'A Security retainer is required',
                  min: {
                    value: minHourlyRate,
                    message: `The minimum Security retainer is ${minPayableAmount}`,
                  },
                }}
                render={({ field }) => (
                  <FormControl fullWidth variant="filled" sx={{ mt: 1.5 }}>
                    <InputLabel htmlFor="payableAmount">
                      Fee Deposit/Security Retainer
                    </InputLabel>
                    <FilledInput
                      {...field}
                      id="payableAmount"
                      fullWidth
                      type="number"
                      onChange={(e) => {
                        const value = e.target.value
                        field.onChange(value === '' ? '' : Number(value))
                      }}
                      startAdornment={
                        <InputAdornment position="start">$</InputAdornment>
                      }
                      error={!!errors.payableAmount}
                      slotProps={{ input: { min: 0, step: 10 } }}
                      required
                    />
                    {errors.payableAmount && (
                      <Typography
                        color="error"
                        variant="caption"
                        sx={{ mt: 1 }}
                      >
                        {errors.payableAmount.message}
                      </Typography>
                    )}
                  </FormControl>
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 8 }}>
              <Controller
                name="details"
                control={control}
                defaultValue=""
                rules={{
                  maxLength: {
                    value: 1000,
                    message: 'Details cannot exceed 1000 characters',
                  },
                  validate: (value) => {
                    const isWordCountValid = validateWordCount(value, {
                      minLength: minDetailsWordCount,
                    })
                    return !isWordCountValid
                      ? `Details must contain at least ${minDetailsWordCount} words`
                      : true
                  },
                }}
                render={({ field }) => (
                  <>
                    <TextField
                      label="Details"
                      variant="filled"
                      fullWidth
                      multiline
                      rows={4}
                      placeholder="Enter details about the quote"
                      error={!!errors.details}
                      {...field}
                    />
                    {errors.details && (
                      <Typography
                        color="error"
                        variant="caption"
                        sx={{ mt: 1 }}
                      >
                        {errors.details.message}
                      </Typography>
                    )}
                  </>
                )}
              />
            </Grid>
          </Grid>
          <Box sx={{ mt: 1, display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              sx={{ mt: 2 }}
            >
              Save
            </Button>
            <Button
              variant="contained"
              color="error"
              data-testid="create-task-cancel-button"
              onClick={onClickCancelButton}
              sx={{ mt: 2, ml: 2 }}
            >
              Cancel
            </Button>
          </Box>
        </form>
      </CardContent>
    </Card>
  )
}

export default HourlyFeeQuoteForm
